import { INSTRUMENT_DIRECTION_LABELS, instrumentDirectionSchema } from "@station/contracts";
import { addDays, type LocalDate, Money } from "@station/core";
import {
  Alert,
  Button,
  Card,
  DataTable,
  EmptyState,
  FormField,
  monthGrid,
  MoneyText,
  Select,
  SkeletonRows,
} from "@station/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { CalendarDays, ChevronLeft, ChevronRight, ScrollText } from "lucide-react";
import { type ReactElement, useId } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import { todayIstanbul } from "../../../features/accounting/shared.js";
import { CounterpartyIdCombobox } from "../../../features/counterparties/counterparty-combobox.js";
import { useInstrumentColumns } from "../../../features/instruments/columns.js";
import { InstrumentPage } from "../../../features/instruments/shared.js";
import { useBankAccounts } from "../../../features/treasury/definitions.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";
import { exportRowsToExcel } from "../../../lib/excel.js";
import { formatDate } from "../../../lib/format.js";
import { queryKeys } from "../../../lib/query.js";

const VIEWS = ["month", "week", "day"] as const;
type View = (typeof VIEWS)[number];
const MONTHS = [
  "Ocak",
  "Şubat",
  "Mart",
  "Nisan",
  "Mayıs",
  "Haziran",
  "Temmuz",
  "Ağustos",
  "Eylül",
  "Ekim",
  "Kasım",
  "Aralık",
];
const WEEKDAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

const date = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);
const searchSchema = z.object({
  ...companySearchShape,
  view: z.enum(VIEWS).optional().catch(undefined),
  anchor: date,
  day: date,
  direction: instrumentDirectionSchema.optional().catch(undefined),
  bankAccountId: z.string().uuid().optional().catch(undefined),
  counterpartyId: z.string().uuid().optional().catch(undefined),
});
type CalendarSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_app/cek-senet/takvim")({
  validateSearch: (search) => searchSchema.parse(search),
  component: CalendarPage,
});

/** Pazartesi ile başlayan hafta. */
function weekStart(day: LocalDate): LocalDate {
  const weekday = (new Date(`${day}T00:00:00Z`).getUTCDay() + 6) % 7;
  return addDays(day, -weekday);
}

function CalendarPage() {
  const { t } = useTranslation();
  return (
    <InstrumentPage title={t("menu.instrumentCalendar")} description={t("instruments.calendar.description")}>
      {({ companyId }) => <CalendarBody companyId={companyId} />}
    </InstrumentPage>
  );
}

function CalendarBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const accounts = useBankAccounts(companyId);
  const today = todayIstanbul();
  const view: View = search.view ?? "month";
  const anchor = (search.anchor ?? today) as LocalDate;
  const setSearch = (patch: Partial<CalendarSearch>) =>
    void navigate({
      search: (previous) =>
        Object.fromEntries(Object.entries({ ...previous, ...patch }).filter(([, value]) => value)),
      replace: true,
    });

  const grid =
    view === "month"
      ? monthGrid(Number(anchor.slice(0, 4)), Number(anchor.slice(5, 7)) - 1)
      : view === "week"
        ? Array.from({ length: 7 }, (_, index) => addDays(weekStart(anchor), index))
        : [anchor];
  const from = grid[0]!;
  const to = grid.at(-1)!;
  const query = {
    companyId,
    from,
    to,
    ...(search.direction ? { direction: search.direction } : {}),
    ...(search.bankAccountId ? { bankAccountId: search.bankAccountId } : {}),
    ...(search.counterpartyId ? { counterpartyId: search.counterpartyId } : {}),
  };
  const calendar = useQuery({
    queryKey: queryKeys.instruments(companyId, "calendar", query),
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.GET("/instrument-reports/calendar", { params: { query } })).data!,
  });
  const byDate = new Map((calendar.data?.days ?? []).map((day) => [day.date, day]));
  const step = view === "month" ? 0 : view === "week" ? 7 : 1;
  const shift = (direction: 1 | -1) =>
    setSearch({
      anchor: view === "month" ? shiftMonth(anchor, direction) : addDays(anchor, direction * step),
      day: undefined,
    });
  const title =
    view === "month"
      ? `${MONTHS[Number(anchor.slice(5, 7)) - 1]} ${anchor.slice(0, 4)}`
      : view === "week"
        ? t("instruments.calendar.weekTitle", { from: formatDate(from), to: formatDate(to) })
        : formatDate(anchor);
  const headingId = useId();

  return (
    <div className="grid gap-4">
      <Card className="grid gap-3 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex gap-1" role="group" aria-label={t("instruments.calendar.viewLabel")}>
            {VIEWS.map((item) => (
              <Button
                key={item}
                variant={view === item ? "secondary" : "ghost"}
                size="sm"
                aria-pressed={view === item}
                onClick={() => setSearch({ view: item })}
              >
                {t(`instruments.calendar.views.${item}`)}
              </Button>
            ))}
          </div>
          <FormField label={t("instruments.fields.direction")} className="w-44">
            {(control) => (
              <Select
                {...control}
                value={search.direction ?? ""}
                onChange={(event) =>
                  setSearch({
                    direction:
                      event.target.value === ""
                        ? undefined
                        : instrumentDirectionSchema.parse(event.target.value),
                  })
                }
              >
                <option value="">{t("common.all")}</option>
                {instrumentDirectionSchema.options.map((direction) => (
                  <option key={direction} value={direction}>
                    {INSTRUMENT_DIRECTION_LABELS[direction]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("instruments.fields.bankAccount")} className="w-56">
            {(control) => (
              <Select
                {...control}
                value={search.bankAccountId ?? ""}
                onChange={(event) => setSearch({ bankAccountId: event.target.value || undefined })}
              >
                <option value="">{t("common.all")}</option>
                {(accounts.data ?? []).map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.bankName} · {account.name}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("instruments.fields.counterparty")} className="w-64">
            {(control) => (
              <CounterpartyIdCombobox
                {...control}
                companyId={companyId}
                counterpartyId={search.counterpartyId ?? null}
                onChange={(id) => setSearch({ counterpartyId: id ?? undefined })}
              />
            )}
          </FormField>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 id={headingId} className="text-base font-semibold">
            {title}
          </h2>
          <nav className="flex items-center gap-1" aria-label={t("instruments.calendar.navigation")}>
            <Button variant="outline" size="sm" onClick={() => shift(-1)}>
              <ChevronLeft aria-hidden="true" />
              {t("instruments.calendar.previous")}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSearch({ anchor: today, day: undefined })}>
              {t("instruments.calendar.today")}
            </Button>
            <Button variant="outline" size="sm" onClick={() => shift(1)}>
              {t("instruments.calendar.next")}
              <ChevronRight aria-hidden="true" />
            </Button>
          </nav>
        </div>

        {calendar.error ? <Alert tone="danger">{errorMessage(calendar.error)}</Alert> : null}
        {calendar.isPending ? (
          <SkeletonRows rows={5} />
        ) : (
          <div className="overflow-x-auto" tabIndex={0} role="group" aria-labelledby={headingId}>
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">{t("instruments.calendar.tableCaption", { title })}</caption>
              <thead>
                <tr>
                  {(view === "day"
                    ? [WEEKDAYS[(new Date(`${anchor}T00:00:00Z`).getUTCDay() + 6) % 7]!]
                    : WEEKDAYS
                  ).map((weekday) => (
                    <th
                      key={weekday}
                      scope="col"
                      className="px-2 py-1 text-xs font-semibold text-muted-foreground"
                    >
                      {weekday}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {chunk(grid, view === "day" ? 1 : 7).map((week) => (
                  <tr key={week[0]}>
                    {week.map((day) => {
                      const totals = byDate.get(day);
                      const inMonth = view !== "month" || day.slice(0, 7) === anchor.slice(0, 7);
                      const isToday = day === today;
                      const isSelected = day === search.day;
                      return (
                        <td key={day} className="p-1 align-top">
                          <button
                            type="button"
                            onClick={() => setSearch({ day })}
                            aria-pressed={isSelected}
                            aria-label={t("instruments.calendar.dayLabel", {
                              date: formatDate(day),
                              receivable: Money.fromMinor(totals?.receivableAmount ?? "0").formatTr(),
                              payable: Money.fromMinor(totals?.payableAmount ?? "0").formatTr(),
                            })}
                            className={[
                              "grid h-24 w-full content-start gap-0.5 rounded-md border p-1.5 text-left text-xs hover:border-primary",
                              inMonth ? "bg-card" : "bg-muted/40 text-muted-foreground",
                              isToday ? "border-primary" : "",
                              isSelected ? "ring-2 ring-primary" : "",
                            ].join(" ")}
                          >
                            <span className="tabular font-semibold">{Number(day.slice(8, 10))}</span>
                            {totals && totals.receivableCount > 0 ? (
                              <span className="text-success">
                                +{Money.fromMinor(totals.receivableAmount).formatTr({ symbol: false })} (
                                {totals.receivableCount})
                              </span>
                            ) : null}
                            {totals && totals.payableCount > 0 ? (
                              <span className="text-destructive">
                                −{Money.fromMinor(totals.payableAmount).formatTr({ symbol: false })} (
                                {totals.payableCount})
                              </span>
                            ) : null}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {search.day ? <DayList companyId={companyId} day={search.day} search={search} /> : null}
    </div>
  );
}

function shiftMonth(anchor: LocalDate, direction: 1 | -1): LocalDate {
  const year = Number(anchor.slice(0, 4));
  const month = Number(anchor.slice(5, 7)) - 1 + direction;
  return new Date(Date.UTC(year, month, 1)).toISOString().slice(0, 10) as LocalDate;
}

function chunk<T>(values: readonly T[], size: number): T[][] {
  const result: T[][] = [];
  for (let index = 0; index < values.length; index += size) result.push(values.slice(index, index + size));
  return result;
}

/** Güne tıklanınca o günün çekleri (liste ucu `dueFrom=dueTo`). */
function DayList({
  companyId,
  day,
  search,
}: {
  companyId: string;
  day: string;
  search: CalendarSearch;
}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const columns = useInstrumentColumns();
  const query = {
    companyId,
    limit: 100,
    dueFrom: day,
    dueTo: day,
    ...(search.direction ? { direction: search.direction } : {}),
    ...(search.bankAccountId ? { bankAccountId: search.bankAccountId } : {}),
    ...(search.counterpartyId ? { counterpartyId: search.counterpartyId } : {}),
  };
  const list = useQuery({
    queryKey: queryKeys.instruments(companyId, "list", query),
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.GET("/instruments", { params: { query } })).data!,
  });
  return (
    <section
      className="grid gap-2"
      aria-label={t("instruments.calendar.dayListTitle", { date: formatDate(day) })}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">
          {t("instruments.calendar.dayListTitle", { date: formatDate(day) })}
        </h2>
        {list.data ? (
          <p className="text-sm">
            {t("instruments.list.totals", { count: list.data.totalCount })}{" "}
            <MoneyText value={list.data.totalAmount} className="font-semibold" />
          </p>
        ) : null}
      </div>
      <DataTable
        caption={t("instruments.calendar.dayListTitle", { date: formatDate(day) })}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={3}
            icon={<CalendarDays />}
            title={t("instruments.calendar.emptyDayTitle")}
            description={t("instruments.calendar.emptyDayDescription")}
          />
        }
        onExport={async (visible) => {
          await exportRowsToExcel({
            fileName: `cek-senet-vade-${day}`,
            sheetName: t("menu.instrumentCalendar"),
            columns: visible,
            rows: list.data?.items ?? [],
          });
        }}
        onRowClick={(row) => void navigate({ to: "/cek-senet/$id", params: { id: row.id } })}
      />
      <p className="text-xs text-muted-foreground">
        <ScrollText className="mr-1 inline size-3" aria-hidden="true" />
        {t("instruments.calendar.dayListHint")}
      </p>
    </section>
  );
}
